import isEmail from 'validator/es/lib/isEmail';
import isMobilePhone from 'validator/es/lib/isMobilePhone';
import { RuleObject } from 'antd/lib/form';
import { StoreValue } from 'antd/lib/form/interface';
import { Dayjs } from 'dayjs';
import { BUSINESS_TYPE, CommonGarbageDetectionOptions, specialInnerCodeMap } from '@yaotai/common-utils';
import { get } from 'lodash-es';
import { COMMON_TIME_FORMAT_END_D, emailReg, MOBILE_PHONE_LOCALE } from '../constants';
import { getByteSize, getUtf8ByteSize } from './common';

const websiteUrlRegex = /^(((ht|f)tps?):\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;

export const checkEmail =
  (required: boolean = true, message?: string) =>
  (rule: RuleObject, value: StoreValue) => {
    if (!value && !required) return Promise.resolve();
    if (!isEmail(value)) {
      return Promise.reject(message || '邮箱格式不正确');
    }
    return Promise.resolve();
  };

export const checkInternationalPhone = () => (rule: RuleObject, value: StoreValue) => {
  if (!isMobilePhone(value, MOBILE_PHONE_LOCALE)) {
    return Promise.reject('联系电话格式不正确');
  }
  return Promise.resolve();
};

export const validateInternationalPhone =
  (required: boolean = true) =>
  (rule: RuleObject, value: StoreValue) => {
    const { phone } = value;
    if (!phone && !required) return Promise.resolve();
    return isMobilePhone(phone) ? Promise.resolve() : Promise.reject('手机格式不正确');
  };

export const phoneInternationalRegForAdmin = /^(\+|\d)[0-9]{7,16}$/;

export const validateInternationalPhoneFormAdmin = (rule: RuleObject, value: StoreValue) => {
  const { phone } = value;
  return phoneInternationalRegForAdmin.test(phone) ? Promise.resolve() : Promise.reject('手机格式不正确');
};

export const validateEmail = (rule: RuleObject, value: StoreValue) => {
  return emailReg.test(value) ? Promise.resolve() : Promise.reject('邮箱格式不正确');
};

export const validateEmailWithoutNull = (errorMessage: string) => (rule: RuleObject, value: StoreValue) => {
  if (!value) return Promise.reject(errorMessage);
  return validateEmail(rule, value);
};

export const checkPattern = (pattern: RegExp, msg: string) => (rule: RuleObject, value: StoreValue) => {
  if (!pattern.test(value)) {
    return Promise.reject(msg);
  }
  return Promise.resolve();
};
export const checkSpace = (errorMsg: string) => (rule: RuleObject, value: StoreValue) => {
  const trimValue = value?.trim();
  if (trimValue?.length === 0) {
    return Promise.reject(errorMsg);
  }
  return Promise.resolve();
};

export const validateVerificationCode =
  ({
    codePhone,
    phone,
    codeFrequentText,
    codeFrequentAndLaterTryText,
    emptyText,
    notGotCodeText,
    errorCodeText,
    fromLogin,
  }: {
    codeFrequentAndLaterTryText?: string;
    codeFrequentText?: string;
    codePhone?: string;
    emptyText?: string;
    errorCodeText?: string;
    fromLogin?: boolean;
    notGotCodeText?: string;
    phone?: string;
  }) =>
  (rule: RuleObject, value: StoreValue) => {
    const codeReg = /^\d{6}$/;
    const isFromBindMobileBtn = sessionStorage.getItem('validate_from_bind_mobile_btn');
    sessionStorage.removeItem('validate_from_bind_mobile_btn');
    if (value === specialInnerCodeMap['getCodeFrequent']) {
      return Promise.reject(codeFrequentText ?? '验证码获取过于频繁');
    } else if (value === specialInnerCodeMap['getCodeFrequentAndLaterTry']) {
      return Promise.reject(codeFrequentAndLaterTryText ?? '验证码获取过于频繁，请 5 分钟后再试');
    } else if (!value && (fromLogin || isFromBindMobileBtn)) return Promise.reject(emptyText ?? '请输入短信验证码');
    else if (phone && codePhone !== phone) {
      return Promise.reject(notGotCodeText ?? '请先获取验证码');
    } else if (value && !codeReg.test(value)) {
      return Promise.reject(errorCodeText ?? '验证码错误');
    } else return Promise.resolve();
  };

// 长期活动后，活动时间分段及支持 24 小时活动。门票的校验范围改为 [当前时间 +10 分钟，活动日期范围最后一天 +1 天的 24:00]
export const checkCodeValidTime = (activityEndTime: Dayjs) => (rule: RuleObject, value: Dayjs[]) => {
  if (!value) return Promise.resolve();
  const endTime = activityEndTime.clone().add(1, 'd').endOf('day');
  const tooLate = value[1]?.startOf('minutes')?.isAfter(endTime);
  if (tooLate) {
    return Promise.reject(`结束时间不能晚于${endTime.format(COMMON_TIME_FORMAT_END_D)} 24:00`);
  }
  return Promise.resolve();
};
export const checkNull = (errorMsg: string) => (rule: RuleObject, value: StoreValue) => {
  if (!value || value.trim().length === 0) return Promise.reject(errorMsg);
  return Promise.resolve();
};

export const checkWebsiteUrl = (errorMsg: string) => (rule: RuleObject, value: StoreValue) => {
  if (!websiteUrlRegex.test(value)) return Promise.reject(errorMsg);
  return Promise.resolve();
};

export const checkByteLength = (length: number, msg?: string) => (rule: RuleObject, value: StoreValue) => {
  if (getByteSize(value) > length) {
    const message = msg ?? `中文字符不超过 ${Math.floor(length / 2)}，英文字符不超过 ${length}`;
    return Promise.reject(message);
  }
  return Promise.resolve();
};

export const checkUtf8ByteLength = (length: number, msg?: string) => (rule: RuleObject, value: StoreValue) => {
  if (getUtf8ByteSize(value) > length) {
    const message = msg ?? `中文字符不超过 ${Math.floor(length / 3)}，英文字符不超过 ${length}`;
    return Promise.reject(message);
  }
  return Promise.resolve();
};

export const checkIsBlank = (errorMsg: string) => (rule: RuleObject, value: StoreValue) => {
  if (value && value.trim().length > 0) return Promise.resolve();
  return Promise.reject(errorMsg || '不能为空');
};

export const checkIsStartOrEndWithBlank = (errorMsg: string) => (rule: RuleObject, value: StoreValue) => {
  if (new RegExp(/^(?!\s)(?!.*\s$)/).test(value)) return Promise.resolve();
  return Promise.reject(errorMsg || '不能以空格开头或结尾');
};

export const garbageDetectionValidator =
  ({ request, type = BUSINESS_TYPE.TEXT, level = 1, msg }: Omit<CommonGarbageDetectionOptions, 'name'>) =>
  async (_: any, value: string | object) => {
    if (!value) return;

    try {
      const response = await request({
        content: typeof value === 'object' ? Object.values(value) : value,
        request_business_type: type!,
        spam_level: level,
      });
      if (response?.code === specialInnerCodeMap.garbageDetection) {
        throw new Error(msg || response?.msg || '检测失败');
      }
    } catch (error) {
      const errorMsg = get(error, 'response.data.msg') || String(error).split(':').slice(-1)[0].trim() || '检测失败';
      throw new Error(errorMsg);
    }
  };
